import remove from "lodash/remove";
import uniqueId from "lodash/uniqueId";

import { StatusType } from "./types";

/** The events that occur within the Arc Reactor. */
enum EventType {
  Mouseout = "mouseout",
  Mouseover = "mouseover",
}

/** The values required by any listener instance. */
interface Listener {
  f: (s: StatusType) => void;
  deletionKey: string;
}

/** Contains a cache of listeners mapped to their associated events. */
type ListenerCache = {
  [K in EventType]: Listener[]
};

/** Provides rudimentary event management functionality. */
class Events {
  public readonly listenerCache: ListenerCache = {
    mouseout: [],
    mouseover: [],
  };

  /**
   * Registers a function to listen for a certain type of event.
   * @param on The event to trigger the function on.
   * @param f The function to trigger.
   */
  public registerListener(on: EventType, f: (s: StatusType) => void): string {
    const deletionKey = uniqueId();
    this.listenerCache[on].push({ deletionKey, f });
    return deletionKey;
  }

  /**
   * Unregisters a function associated with a given event and deletion key.
   * @param on The event the function was originally registered with.
   * @param deletionKey The key given for use in deletion.
   */
  public unregisterListener(on: EventType, deletionKey: string): void {
    remove(this.listenerCache[on], ({ deletionKey: dKey }) => deletionKey === dKey);
  }

  /**
   * Emits an event for a given status type.
   * @param event The event being emitted.
   * @param status The status it was associated with.
   */
  public emit(event: EventType, status: StatusType): void {
    for (const { f } of this.listenerCache[event]) {
      f(status);
    }
  }
}

export default Events;
export {
  EventType,
};
